// Keep the FAQ outside the two-column product grid so it follows the grid's
// natural height and cannot escape the native sticky-gallery boundary.
import { ProductSelectEvent } from '@shopify/events';

const getFAQHost = grid => {
  let host = grid.nextElementSibling;
  if (!host?.matches('.zam-faq-host')) {
    host = document.createElement('div');
    host.className = 'zam-faq-host';
    grid.after(host);
  }
  return host;
};

const placeFAQAfterGrid = (root = document) => {
  root.querySelectorAll('.product-information__grid').forEach(grid => {
    const faq = grid.querySelector('.zam-faq-outer');
    if (faq) getFAQHost(grid).replaceChildren(faq);
  });
};

const faqAlignmentControllers = new WeakMap();
const findFAQAlignmentAnchor = grid =>
  grid.querySelector('.zm-fragrance-profile, [data-zm-profile]') ??
  grid.querySelector('.zm-product-description, [data-zm-description], [data-zm-customer-information]') ??
  grid.querySelector('.product-details > .group-block') ??
  grid.querySelector('.product-details');

const installFAQAlignment = grid => {
  const host = grid.nextElementSibling?.matches('.zam-faq-host') ? grid.nextElementSibling : null;
  if (!host || faqAlignmentControllers.has(host)) return;

  let frame = 0;
  let observedAnchor = null;
  const update = () => {
    frame = 0;
    if (!host.isConnected || !grid.isConnected) return;
    const anchor = findFAQAlignmentAnchor(grid);
    if (anchor !== observedAnchor) {
      if (observedAnchor) resizeObserver.unobserve(observedAnchor);
      observedAnchor = anchor;
      if (observedAnchor) resizeObserver.observe(observedAnchor);
    }
    if (!anchor) return;

    const anchorRect = anchor.getBoundingClientRect();
    const hostRect = host.getBoundingClientRect();
    if (!anchorRect.width || !hostRect.width) return;
    const width = `${anchorRect.width}px`;
    const offset = `${anchorRect.left - hostRect.left}px`;
    if (host.style.getPropertyValue('--zam-faq-profile-width') !== width) {
      host.style.setProperty('--zam-faq-profile-width', width);
    }
    if (host.style.getPropertyValue('--zam-faq-profile-offset') !== offset) {
      host.style.setProperty('--zam-faq-profile-offset', offset);
    }
  };
  const schedule = () => {
    if (!frame) frame = requestAnimationFrame(update);
  };
  const resizeObserver = new ResizeObserver(schedule);
  resizeObserver.observe(grid);
  const mutationObserver = new MutationObserver(schedule);
  mutationObserver.observe(grid, { childList: true, subtree: true });
  window.addEventListener('resize', schedule, { passive: true });
  faqAlignmentControllers.set(host, { resizeObserver, mutationObserver, schedule });
  schedule();
};

const syncFAQAlignments = (root = document) => {
  root.querySelectorAll('.product-information__grid').forEach(installFAQAlignment);
};

const reviewSummaryObservers = new WeakMap();
const syncReviewSummaries = (root = document) => {
  root.querySelectorAll('.zm-review-summary').forEach(summary => {
    const showSummary = () => {
      const count = summary.querySelector('.junip-product-summary-review-count')?.textContent ?? '';
      const digits = count.replace(/[^\d]/g, '');
      summary.hidden = !digits || Number(digits) < 1;
    };

    showSummary();
    if (!reviewSummaryObservers.has(summary)) {
      const observer = new MutationObserver(showSummary);
      observer.observe(summary, { childList: true, subtree: true, characterData: true });
      reviewSummaryObservers.set(summary, observer);
    }
  });
};

const profilePreviewVariant = (() => {
  const theme = window.Shopify?.theme;
  if (Number(theme?.id) !== 208548266321 || theme?.role === 'main') return null;
  const requested = new URL(window.location.href).searchParams.get('zm_profile_style');
  return requested === 'bar' || requested === 'soft' ? requested : null;
})();

const applyProfilePreviewVariant = (root = document) => {
  if (!profilePreviewVariant) return;
  if (root.matches?.('[data-zm-profile]')) root.dataset.zmProfileVariant = profilePreviewVariant;
  root.querySelectorAll?.('[data-zm-profile]').forEach(profile => {
    profile.dataset.zmProfileVariant = profilePreviewVariant;
  });
};

const observeProfilePreviewRenders = () => {
  if (!profilePreviewVariant || !document.documentElement) return;
  const observer = new MutationObserver(records => {
    records.forEach(record => {
      record.addedNodes.forEach(node => {
        if (node.nodeType === Node.ELEMENT_NODE) applyProfilePreviewVariant(node);
      });
    });
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
};

if (!window.zamooriProductStandardInstalled) {
  window.zamooriProductStandardInstalled = true;
  placeFAQAfterGrid();
  syncFAQAlignments();
  syncReviewSummaries();
  applyProfilePreviewVariant();
  observeProfilePreviewRenders();

  document.addEventListener(ProductSelectEvent.eventName, async event => {
    try {
      const selection = await event.promise;
      const renderedHTML = selection?.detail?.html;
      const section = event.target?.closest?.('.shopify-section');
      const grid = section?.querySelector('.product-information__grid');
      const context = grid?.querySelector('[data-zm-customer-information]');
      if (!renderedHTML || !grid || !context) return;

      const matchesContext = candidate =>
        candidate.dataset.sectionId === context.dataset.sectionId &&
        candidate.dataset.productId === context.dataset.productId &&
        candidate.dataset.contextRole === context.dataset.contextRole;
      if (![...renderedHTML.querySelectorAll('[data-zm-customer-information]')].some(matchesContext)) return;

      // The context controller also awaits event.promise, then replaces its
      // wrapper synchronously. The next frame sees that fresh wrapper; no
      // guessed timeout or observer feedback is needed.
      requestAnimationFrame(() => {
        if (!grid.isConnected) return;
        applyProfilePreviewVariant(section);
        const currentContext = [...grid.querySelectorAll('[data-zm-customer-information]')].find(matchesContext);
        const freshFAQ = currentContext?.querySelector('.zam-faq-outer');
        const host = getFAQHost(grid);
        const oldFAQ = host.querySelector('.zam-faq-outer');
        if (freshFAQ === oldFAQ) return;

        const openKeys = new Set(
          oldFAQ
            ? [...oldFAQ.querySelectorAll('details[open][data-zm-customer-key]')]
                .map(detail => detail.dataset.zmCustomerKey)
            : [],
        );
        oldFAQ?.remove();
        if (freshFAQ) {
          freshFAQ.querySelectorAll('details[data-zm-customer-key]').forEach(detail => {
            detail.open = openKeys.has(detail.dataset.zmCustomerKey);
          });
          host.append(freshFAQ);
        }
        syncFAQAlignments(section);
      });
    } catch (error) {
      if (error?.name !== 'AbortError') console.error('Zamoori FAQ refresh failed', error);
    }
  });
  document.addEventListener('shopify:section:load', event => {
    placeFAQAfterGrid(event.target);
    syncFAQAlignments(event.target);
    syncReviewSummaries(event.target);
    applyProfilePreviewVariant(event.target);
  });
}
